import type {
  MasterSearchEntityType,
  MasterSearchItem
} from "api/masterSearch";

interface MasterSearchProductFlags {
  isBoomCRM: boolean;
  isBoomScreen: boolean;
  isRentReporting: boolean;
  isLockboxes: boolean;
}

const PRODUCT_GATES: Record<
  MasterSearchEntityType,
  (flags: MasterSearchProductFlags) => boolean
> = {
  lead: (flags) => flags.isBoomCRM,
  contact: (flags) => flags.isBoomCRM,
  screen_applicant: (flags) => flags.isBoomScreen,
  report_renter: (flags) => flags.isRentReporting,
  listing: (flags) => flags.isBoomCRM,
  lockbox: (flags) => flags.isBoomCRM && flags.isLockboxes
};

const toNumericId = (
  value: string | number | null | undefined
): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const listingRoute = (item: MasterSearchItem): string | null => {
  const propertyId = toNumericId(item.context?.property_id);
  const unitId = toNumericId(item.context?.unit_id);
  if (propertyId && unitId) return `/properties/${propertyId}/units/${unitId}`;
  if (propertyId) return `/properties/${propertyId}`;
  return null;
};

const routeFor = (item: MasterSearchItem): string | null => {
  switch (item.type) {
    case "lead":
      return `/leads/${item.id}`;
    case "contact":
      return `/contacts/${item.id}`;
    case "screen_applicant": {
      const applicationId = toNumericId(item.context?.application_id);
      return applicationId ? `/applications/${applicationId}` : null;
    }
    case "report_renter":
      return `/rent-reporting/${item.id}`;
    case "listing":
      return listingRoute(item);
    case "lockbox":
      return `/lockboxes?lockboxId=${item.id}`;
    default:
      return null;
  }
};

// Results must never deep-link into a product route the partner lacks — the
// route table filters those out, so the link would fall through to Not Found.
export const buildDetailRoute = (
  item: MasterSearchItem,
  flags: MasterSearchProductFlags
): string | null => (PRODUCT_GATES[item.type]?.(flags) ? routeFor(item) : null);

/**
 * Types this partner can actually open. The server gates groups per product,
 * but a couple of client routes need a second flag (lockboxes), so ungated
 * groups are dropped before render rather than rendered as dead rows.
 */
export const routableTypes = (
  flags: MasterSearchProductFlags
): MasterSearchEntityType[] =>
  (Object.keys(PRODUCT_GATES) as MasterSearchEntityType[]).filter((type) =>
    PRODUCT_GATES[type](flags)
  );
